import React, {useState, useRef, useEffect} from "react";
import {
    VStack, HStack, Button, Box, Text, Spinner, Table, Heading,
    Container, Separator, Badge, Card, Icon, Stack,
} from "@chakra-ui/react";
import * as XLSX from "xlsx";
import {HiDocumentReport, HiCheckCircle, HiDatabase} from "react-icons/hi";
import {LuPlay} from "react-icons/lu";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";

const baseUrlWs = process.env.REACT_APP_API_URL_WS;

export default function DemoFinancialAnalysis() {
    const [fileData, setFileData] = useState(null); // Stores the parsed JSON data
    const [analysisText, setAnalysisText] = useState("");
    const [financialRatios, setFinancialRatios] = useState(null);
    const [loading, setLoading] = useState(false);
    const [isFetchingFile, setIsFetchingFile] = useState(true);

    const wsRef = useRef(null);
    const fullTextRef = useRef("");
    const ratiosParsedRef = useRef(false);

    // 1. Fetch and Parse the Excel file from /public automatically
    useEffect(() => {
        const loadDemoFile = async () => {
            try {
                // Ensure "does_financials.xlsx" exists in your public folder
                const response = await fetch("/does_financials.xlsx");
                if (!response.ok) {
                    throw new Error(`HTTP error! status: ${response.status}`);
                }
                const arrayBuffer = await response.arrayBuffer();

                const workbook = XLSX.read(arrayBuffer, {type: "array"});
                const allSheets = {};
                workbook.SheetNames.forEach((name) => {
                    allSheets[name] = XLSX.utils.sheet_to_json(workbook.Sheets[name], {
                        defval: null,
                        raw: false,
                    });
                });

                setFileData(allSheets);
            } catch (err) {
                console.error("Error loading file:", err);
            } finally {
                setIsFetchingFile(false);
            }
        };

        loadDemoFile();
    }, []);

    const handleAnalyze = async () => {
        if (!fileData) return;

        setAnalysisText("");
        setFinancialRatios(null);
        setLoading(true);
        fullTextRef.current = "";
        ratiosParsedRef.current = false;

        try {
            const wsUrl = `${baseUrlWs}/applicant/financials/ws/analyze`;
            wsRef.current = new WebSocket(wsUrl);

            wsRef.current.onopen = () => {
                wsRef.current.send(JSON.stringify({
                    financial_data: fileData, // Send the pre-parsed data
                    analysis_type: "financial_statement",
                }));
            };

            wsRef.current.onmessage = (event) => {
                const data = JSON.parse(event.data);
                if (data.type === "chunk") {
                    fullTextRef.current += data.chunk;

                    if (!ratiosParsedRef.current) {
                        const match = fullTextRef.current.match(/<RATIOS_JSON>([\s\S]*?)<\/RATIOS_JSON>/);
                        if (match) {
                            try {
                                setFinancialRatios(JSON.parse(match[1]));
                                ratiosParsedRef.current = true;
                            } catch (e) {
                                console.error(e);
                            }
                        }
                    }

                    const cleanDisplay = fullTextRef.current
                        .replace(/<RATIOS_JSON>[\s\S]*?<\/RATIOS_JSON>/g, "")
                        .replace(/<RATIOS_JSON>[\s\S]*/g, "")
                        .trim();

                    setAnalysisText(cleanDisplay);
                }

                if (data.status === "complete" || data.status === "error") {
                    setLoading(false);
                    wsRef.current.close();
                }
            };
        } catch (err) {
            setLoading(false);
        }
    };

    const renderBoldText = (text) => {
        return text.split(/(\*\*.*?\*\*)/g).map((part, index) => {
            if (part.startsWith("**") && part.endsWith("**")) {
                return <strong key={index}>{part.slice(2, -2)}</strong>;
            }
            return part;
        });
    };

    const formatMetricKey = (key) => {
        // List of keys that should remain all uppercase
        const acronyms = ["roa", "roe", "ebitda", "roi", "ros", "dscr"];

        if (acronyms.includes(key.toLowerCase())) {
            return key.toUpperCase();
        }

        // Otherwise: replace underscores with spaces and capitalize the first letter
        const spaced = key.replace(/_/g, " ");
        return spaced.charAt(0).toUpperCase() + spaced.slice(1);
    };

    return (
        <Container maxW="6xl" py={10}>
            <VStack align="stretch" gap={8}>
                <HStack justify="space-between" align="flex-end">
                    <VStack align="start" gap={1}>
                        <Heading size="2xl" tracking="tight">Demo: Financials</Heading>
                        <Text color="fg.muted">Previewing with "does_financials.xlsx"</Text>
                    </VStack>
                    <Badge variant="outline" colorPalette="blue" size="lg">
                        Agxes Financial Engine
                    </Badge>
                </HStack>

                <Separator/>

                {/* Input Card: Simplified to just a Trigger Button */}
                <Card.Root variant="outline" borderRadius="lg" bg="blue.50/30">
                    <Card.Body p={4}>
                        <Stack
                            direction={{base: "column", md: "row"}}
                            gap={4}
                            width="full"
                            align="center"
                            justify="space-between"
                        >
                            <HStack gap={3}>
                                <Box p={2} bg="blue.100" borderRadius="md" color="blue.700">
                                    <HiDatabase size="20px"/>
                                </Box>
                                <VStack align="start" gap={0}>
                                    <Text fontWeight="bold" fontSize="sm">John Doe's Financials</Text>
                                    <Text fontSize="xs" color="fg.muted">Financial Statements v2.1</Text>
                                </VStack>
                            </HStack>

                            <Button
                                colorPalette="blue"
                                variant="solid"
                                size="lg"
                                width={{base: "full", md: "auto"}}
                                px={10}
                                onClick={handleAnalyze}
                                loading={loading || isFetchingFile}
                                disabled={!fileData}
                                boxShadow="sm"
                            >
                                <LuPlay/>
                                Run Analysis
                            </Button>
                        </Stack>
                    </Card.Body>
                </Card.Root>

                {/* Results Grid (remains mostly the same) */}
                {(loading || analysisText || financialRatios) && (
                    <HStack align="start" gap={6} flexDir={{base: "column", lg: "row"}}>
                        {/* Left Column: Metrics */}
                        <VStack align="stretch" flex="1" gap={6}>
                            <Card.Root>
                                <Card.Header>
                                    <HStack justify="space-between">
                                        <Heading size="md">Key Financial Metrics</Heading>
                                        {financialRatios && <Icon as={HiCheckCircle} color="green.500"/>}
                                    </HStack>
                                </Card.Header>
                                <Card.Body>
                                    {!financialRatios && loading ? (
                                        <VStack py={10}><Spinner color="blue.500"/></VStack>
                                    ) : (
                                        <Table.Root size="sm" variant="line">
                                            <Table.Header>
                                                <Table.Row>
                                                    <Table.ColumnHeader>Metric</Table.ColumnHeader>
                                                    <Table.ColumnHeader textAlign="end">Value</Table.ColumnHeader>
                                                </Table.Row>
                                            </Table.Header>
                                            <Table.Body>
                                                {financialRatios ? Object.entries(financialRatios).map(([key, value]) => (
                                                    <Table.Row key={key}>
                                                        <Table.Cell fontWeight="medium">
                                                            {formatMetricKey(key)}
                                                        </Table.Cell>
                                                        <Table.Cell textAlign="end" color="blue.600" fontWeight="bold">
                                                            {/* Optional: Add percentage sign if value is a ratio */}
                                                            {value ?? "—"}
                                                        </Table.Cell>
                                                    </Table.Row>
                                                )) : (
                                                    <Table.Row>
                                                        <Table.Cell colSpan={2} textAlign="center" py={4}
                                                                    color="fg.muted">
                                                            Start analysis to view ratios
                                                        </Table.Cell>
                                                    </Table.Row>
                                                )}
                                            </Table.Body>
                                        </Table.Root>
                                    )}
                                </Card.Body>
                            </Card.Root>
                        </VStack>

                        {/* Right Column: AI Narrative */}
                        <VStack align="stretch" flex="2">
                            <Card.Root>
                                <Card.Header>
                                    <HStack>
                                        <HiDocumentReport color="#3182ce"/>
                                        <Heading size="md">Financials Summary</Heading>
                                    </HStack>
                                </Card.Header>
                                <Card.Body bg="white" borderRadius="md" minH="400px" px={8} py={6}>
                                    {analysisText ? (
                                        <Box className="markdown-container" color="gray.800">
                                            <ReactMarkdown remarkPlugins={[remarkGfm]}
                                                           components={{
                                                               h3: ({...props}) => (
                                                                   <Heading size="md" mt={6} mb={3} {...props} />
                                                               ),
                                                               h4: ({...props}) => (
                                                                   <Heading size="sm" mt={4} mb={2} {...props} />
                                                               ),
                                                               p: ({...props}) => (
                                                                   <Text mb={3} fontSize="sm" {...props} />
                                                               ),

                                                               // 👇 IMPORTANT CHANGE
                                                               ul: ({children}) => (
                                                                   <Box
                                                                       as="ul"
                                                                       listStyleType="none" // Remove default browser bullets
                                                                       ps={0}               // Reset padding-start
                                                                       mb={4}
                                                                       mt={2}
                                                                   >
                                                                       {children}
                                                                   </Box>
                                                               ),

                                                               li: ({children}) => (
                                                                   <Box
                                                                       as="li"
                                                                       fontSize="sm"
                                                                       position="relative"
                                                                       ps={6} // Space for the custom bullet
                                                                       mb={2}
                                                                       _before={{
                                                                           content: '""',
                                                                           display: "inline-block",
                                                                           position: "absolute",
                                                                           left: 1,
                                                                           top: "0.5em", // Vertically centers with the first line
                                                                           width: "6px",
                                                                           height: "6px",
                                                                           borderRadius: "full",
                                                                           bg: "black", // Your brand color here
                                                                       }}
                                                                   >
                                                                       {children}
                                                                   </Box>
                                                               ),

                                                               strong: ({...props}) => (
                                                                   <Text as="span" fontWeight="semibold" {...props} />
                                                               ),
                                                               hr: () => (
                                                                   <Box my={6} borderTop="1px solid"
                                                                        borderColor="gray.200"/>
                                                               ),
                                                           }}>
                                                {analysisText}
                                            </ReactMarkdown>
                                        </Box>
                                    ) : (
                                        <VStack py={20} color="gray.400">
                                            {loading ? <Spinner/> :
                                                <Text fontStyle="italic">Click "Run Analysis" to begin...</Text>}
                                        </VStack>
                                    )}
                                </Card.Body>
                            </Card.Root>
                        </VStack>
                    </HStack>
                )}
            </VStack>
        </Container>
    );
}